import React, { FC, useCallback, useEffect } from 'react'
import { useStoreId } from '@moonpig/web-core-stores'
import {
  AddedToBasketEvent,
  DispatchCutOffQuery,
  useBasket,
  useHandleAddToBasket,
  PageTypeEnum,
  ProductTileProduct,
} from '@moonpig/web-shared-products'
import { getBrowserCookies } from '@moonpig/web-core-browser-storage'
import cookieValue from 'querystring'
import { useCustomerId } from '@moonpig/web-shared-utils'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { logger } from '@moonpig/web-core-monitoring'
import { Box } from '@moonpig/launchpad-components'
import { Header as GalleryHeader } from '../Gallery'
import { SEARCH_TITLE_FOR_TRACKING } from '../../constants'
import { SearchResults } from '../SearchResults/SearchResults'
import { useFindLocaleText } from '../../text-localisation'
import {
  capitaliseFirstCharacter,
  useResetOffset,
  useSetUIHCookie,
} from '../../utils'
import { useSearchStore } from '../../store/SearchStore'
import { SearchHeader } from './SearchHeader'

const SearchTitle = SEARCH_TITLE_FOR_TRACKING

const capitaliseFirstLetters = (searchTerm: string): string => {
  return searchTerm.split(' ').map(capitaliseFirstCharacter).join(' ')
}

type SearchProps = {
  products: ProductTileProduct[]
  experiments?: { [key: string]: string } | undefined
  suggestions: ProductTileProduct[]
  loading: boolean
  dispatchCutOffData?: DispatchCutOffQuery
  customerId?: string
  metaTitle: string
  groupCardProject?: string
  isDynamic: boolean
  totalClickRankDocumentCount: number
  totalCount: number
  department: DepartmentsEnum
}

export const Search: FC<SearchProps> = ({
  products,
  experiments,
  suggestions,
  dispatchCutOffData,
  metaTitle,
  groupCardProject,
  isDynamic,
  totalClickRankDocumentCount,
  totalCount,
  department,
}) => {
  useResetOffset(0)
  const storeId = useStoreId()
  const { setUIHCookie } = useSetUIHCookie()
  const selectedFilters = useSearchStore(store => store.selectedFilters)
  const preAppliedFilters = useSearchStore(store => store.preAppliedFilters)
  const searchContext = useSearchStore(store => store.searchContext)

  const localiseText = useFindLocaleText()
  const soldOutMessage = localiseText('find.sold_out')
  const itsNotYouMessage = localiseText('find.its_not_you')

  const [_, { setLoading, setError }] = useBasket()

  const title = capitaliseFirstLetters(searchContext.searchTerm)

  const shouldDisplayZeroResults =
    products && products.length === 0 && suggestions && suggestions.length !== 0

  const { responsiveSettings } = getBrowserCookies()
  const { EnableInternalUserMode: enableInternalUserMode = 'false' } =
    cookieValue.parse(responsiveSettings)
  const isInternalUser = /^true$/i.test(enableInternalUserMode as string)

  const customerId = useCustomerId()

  const { handleAddToBasket, addingToBasket } = useHandleAddToBasket({
    pageLocation: 'search',
    soldOutMessage,
    itsNotYouMessage,
    groupCardProject,
  })

  useEffect(() => {
    setLoading(addingToBasket)
  }, [addingToBasket, setLoading])

  const onAddedToBasket = useCallback(
    async (e: AddedToBasketEvent) => {
      const {
        product,
        variant,
        quantity,
        selectedAddon,
        buttonActionName,
        requiresEditingValue,
      } = e
      const allAppliedFilters = [
        ...selectedFilters.map(f => ({ facetKey: f.id, group: f.parent })),
        ...preAppliedFilters.map(f => ({
          facetKey: f.key,
          group: f.group,
        })),
      ]
      setUIHCookie({ product, facets: allAppliedFilters })

      try {
        await handleAddToBasket({
          quantity,
          product,
          variant,
          filters: allAppliedFilters,
          experiments,
          searchTerm: searchContext.searchTerm,
          selectedAddon,
          itemIndex: product?.productIndex || 0,
          productsLength:
            products.length || /* istanbul ignore next*/ suggestions.length,
          buttonActionName,
          requiresEditingValue,
        })
      } catch (error) {
        logger.fixToday('Failure adding product to basket', error)
        setError(true)
      }
    },
    [
      setUIHCookie,
      handleAddToBasket,
      selectedFilters,
      preAppliedFilters,
      experiments,
      searchContext.searchTerm,
      products.length,
      suggestions.length,
      setError,
    ],
  )

  const renderHeader = () => {
    if (isDynamic) {
      return (
        <Box pl={{ xs: 6, lg: 10, xxl: 12 }}>
          <GalleryHeader title={title} />
        </Box>
      )
    }
    return (
      <SearchHeader
        zeroResults={shouldDisplayZeroResults}
        searchContext={searchContext}
        department={department}
      />
    )
  }

  const getBreadcrumb = () => {
    switch (searchContext.departments[0]) {
      case 'ALL_FLOWERS_AND_PLANTS':
        return {
          url: `/${storeId}/flowers_and_plants`,
          label: localiseText('common.flowers_and_plants'),
        }
      case 'ALL_GIFTS':
        return {
          url: `/${storeId}/gifts`,

          label: localiseText('common.gifts'),
        }
      default:
        return {
          url: `/${storeId}/personalised-cards`,
          label: localiseText('common.personalised_cards'),
        }
    }
  }

  const breadcrumbs = isDynamic
    ? [
        {
          url: `/${storeId}/`,
          label: localiseText('find.home'),
        },
        getBreadcrumb(),
      ]
    : []

  return (
    <SearchResults
      renderHeader={renderHeader}
      searchContext={searchContext}
      totalCount={totalCount}
      products={products}
      zeroResults={shouldDisplayZeroResults}
      experiments={experiments}
      suggestions={suggestions}
      dispatchCutOffData={dispatchCutOffData}
      breadcrumbs={breadcrumbs}
      isInternalUser={isInternalUser}
      addingToBasket={addingToBasket}
      onAddedToBasket={onAddedToBasket}
      searchTitle={isDynamic ? title : SearchTitle}
      pageType={isDynamic ? PageTypeEnum.DYNAMIC : PageTypeEnum.SEARCH}
      customerId={customerId}
      metaTitle={metaTitle}
      groupCardProject={groupCardProject}
      totalClickRankDocumentCount={totalClickRankDocumentCount}
    />
  )
}
